import classNames from 'classnames'
import { Link } from 'gatsby'
import { Image } from 'src/components/ui/Image'
import { Container } from 'src/components/department/Container'
import { useInView } from 'react-intersection-observer'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import type { BannerFullProps } from 'src/components/department/types/banner-full'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'

import { useBannerFull } from './useBannerFull'

export function BannerFull(data: BannerFullProps) {
  const { ref } = useInView({
    threshold: 0.7,
    triggerOnce: true,
    onChange: (inView) => {
      if (!inView) {
        return
      }

      sendPromoFeatureViewedEvent()
    },
  })

  const { validateProps } = useBannerFull()

  const propsIsValid = validateProps(data)

  if (!propsIsValid) {
    return <></>
  }

  const {
    enableSession,
    BannersFull: { image, linkURL, Texto, textColor },
    section,
    locationOnPage,
  } = data

  const sendPromoFeatureViewedEvent = () => {
    if (!locationOnPage || !section) {
      return
    }

    const eventData = {
      ...getStandardObject(),
      ...getFeatureObject({
        section,
        locationOnPage,
        name: Texto,
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  if (!enableSession) {
    return null
  }

  const standardObject = getStandardObject()

  const sendAmplitudeClickEvent = () => {
    if (!locationOnPage || !section) {
      return
    }

    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: Texto ?? '',
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
        'promotional element name': Texto ?? '',
        'promotional element index': 0,
      },
    })
  }

  return (
    <Container
      className="py-[40px] restructure-tablet:py-[48px] restructure-small-desktop:py-[60px] px-4"
      data-testid="BannerFullDepartment"
      ref={ref}
    >
      <Link
        className="relative flex-1 min-h-[328px] restructure-tablet:min-h-[310px] restructure-small-desktop:min-h-[500px] rounded-2xl bg-restructure-background-primary flex items-end"
        to={linkURL}
        data-testid="LinkBanner"
        onClick={() => sendAmplitudeClickEvent()}
      >
        <div className="flex flex-col-reverse w-full restructure-tablet:flex-row gap-md restructure-small-desktop:gap-md">
          <span className="absolute top-0 block w-full h-full rounded-2xl bg-linearCard z-10" />
          <Image
            data-testid="imageMobile"
            src={image.mobile}
            alt={Texto}
            width={328}
            height={328}
            className="object-cover absolute top-0 left-0 w-full h-full rounded-md restructure-tablet:hidden"
          />
          <Image
            data-testid="imageTablet"
            src={image.tablet}
            alt={Texto}
            width={794}
            height={310}
            className="object-cover absolute top-0 left-0 w-full h-full rounded-md hidden restructure-tablet:block restructure-small-desktop:hidden"
          />
          <Image
            data-testid="imageDesktop"
            src={image.desktop}
            alt={Texto}
            width={1250}
            height={500}
            className="object-cover absolute top-0 left-0 w-full h-full rounded-md hidden restructure-small-desktop:block"
          />
          <div className="flex w-full p-6 z-20 items-center justify-between">
            {Texto !== '' && (
              <h2
                data-testid="titleBanner"
                className={`mobile-heading-title3  restructure-tablet:mobile-heading-title2 desktop-heading-title2 ${
                  textColor && textColor === 'branco'
                    ? 'text-restructure-primary-inverted'
                    : 'text-restructure-primary'
                }`}
              >
                {Texto}
              </h2>
            )}
            <button
              title={`Botão ${Texto}`}
              type="button"
              className={classNames(
                'h-[64px] w-[64px] rounded-round desktop-body-semibold-text2 items-center justify-center gap-xs hidden restructure-small-desktop:flex hover:bg-opacity-5 hover:bg-restructure-background-primary border-sm active:bg-opacity-10 border-restructure-border-primary'
              )}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 26 21"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.25 12C4.25 11.5858 4.58579 11.25 5 11.25H19C19.4142 11.25 19.75 11.5858 19.75 12C19.75 12.4142 19.4142 12.75 19 12.75H5C4.58579 12.75 4.25 12.4142 4.25 12Z"
                  fill="white"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.4697 4.46967C11.7626 4.17678 12.2374 4.17678 12.5303 4.46967L19.5303 11.4697C19.8232 11.7626 19.8232 12.2374 19.5303 12.5303L12.5303 19.5303C12.2374 19.8232 11.7626 19.8232 11.4697 19.5303C11.1768 19.2374 11.1768 18.7626 11.4697 18.4697L17.9393 12L11.4697 5.53033C11.1768 5.23744 11.1768 4.76256 11.4697 4.46967Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>
      </Link>
    </Container>
  )
}
